import { format } from 'date-fns';

import { currency } from 'utils/formats';

export type StudentActivityEventType =
  | 'joined'
  | 'registration'
  | 'confirmation'
  | 'online'
  | 'payment'
  | 'certificate';

export type StudentActivityEvent = {
  id: string;
  date: Date;
  type: StudentActivityEventType;
  label: string;
  title: string;
  href?: string;
  certificateUrl?: string;
};

type ActivityUser = {
  id: string;
  created_at?: string | null;
  given_name?: string | null;
  family_name?: string | null;
};

type ActivityRegistration = {
  id: string;
  event_id?: string | null;
  created_at: string;
  confirmed_at?: string | null;
  type?: string | null;
  model_session_purchase_id?: string | null;
  event?: { id?: string; detail?: { title?: string | null } | null } | null;
};

type ActivityOnlineRegistration = {
  id: string;
  created_at: string;
  coassemble_course_id?: string | null;
  data?: { course?: { title?: string | null } } | null;
};

type ActivityPayment = {
  id: string;
  amount?: number | null;
  created_at: string;
};

type ActivityCertificate = {
  id: string;
  created_at: string;
  url?: string | null;
  event?: { detail?: { title?: string | null } | null } | null;
};

export type StudentActivityEventsData = {
  user?: ActivityUser | ActivityUser[] | null;
  registration?: ActivityRegistration[];
  online_registration?: ActivityOnlineRegistration[];
  payment?: ActivityPayment[];
  certificate?: ActivityCertificate[];
};

function displayName(user?: ActivityUser | null, fallback?: string) {
  const name = [user?.given_name, user?.family_name].filter(Boolean).join(' ');
  return name || fallback || 'Student';
}

function toDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function buildStudentActivityEvents(
  data: StudentActivityEventsData | undefined,
  fallbackName?: string,
): StudentActivityEvent[] {
  if (!data) return [];

  const user = Array.isArray(data.user) ? data.user[0] : data.user;
  const name = displayName(user, fallbackName);
  const events: StudentActivityEvent[] = [];

  const joinedAt = toDate(user?.created_at);
  if (user && joinedAt) {
    events.push({
      id: `joined-${user.id}`,
      date: joinedAt,
      type: 'joined',
      label: 'Joined',
      title: `${name} joined the Cosmetic College`,
    });
  }

  (data.registration || []).forEach((registration) => {
    const createdAt = toDate(registration.created_at);
    const courseTitle = registration.event?.detail?.title || 'a course';
    const eventId = registration.event?.id || registration.event_id;
    const role = registration.model_session_purchase_id
      ? ' as a model'
      : registration.type === 'STUDENT'
        ? ' as a student'
        : '';

    if (createdAt) {
      events.push({
        id: `registration-${registration.id}`,
        date: createdAt,
        type: 'registration',
        label: 'Enrolment',
        title: `${name} enrolled${role} on the ${courseTitle} course on ${format(
          createdAt,
          'dd/MM/yyyy',
        )}`,
        href: eventId ? `/events/${eventId}` : undefined,
      });
    }

    const confirmedAt = toDate(registration.confirmed_at);
    if (confirmedAt) {
      events.push({
        id: `registration-confirmed-${registration.id}`,
        date: confirmedAt,
        type: 'confirmation',
        label: 'Confirmed',
        title: `${name} was confirmed on the ${courseTitle} course on ${format(
          confirmedAt,
          'dd/MM/yyyy',
        )}`,
        href: eventId ? `/events/${eventId}` : undefined,
      });
    }
  });

  (data.online_registration || []).forEach((registration) => {
    const createdAt = toDate(registration.created_at);
    if (!createdAt) return;

    events.push({
      id: `online-${registration.id}`,
      date: createdAt,
      type: 'online',
      label: 'E-learning',
      title: `${name} enrolled on the ${
        registration.data?.course?.title || 'online'
      } course on ${format(createdAt, 'dd/MM/yyyy')}`,
      href: registration.coassemble_course_id
        ? `https://cosmeticcollege.coassemble.com/student/course/${registration.coassemble_course_id}`
        : undefined,
    });
  });

  (data.payment || []).forEach((payment) => {
    const createdAt = toDate(payment.created_at);
    if (!createdAt) return;

    events.push({
      id: `payment-${payment.id}`,
      date: createdAt,
      type: 'payment',
      label: 'Payment',
      title: `${name} made a payment of ${currency(
        payment.amount || 0,
      )} on ${format(createdAt, 'dd/MM/yyyy')}`,
    });
  });

  (data.certificate || []).forEach((certificate) => {
    const createdAt = toDate(certificate.created_at);
    if (!createdAt) return;

    events.push({
      id: `certificate-${certificate.id}`,
      date: createdAt,
      type: 'certificate',
      label: 'Certificate',
      title: `${name} earned a certificate for completing the ${
        certificate.event?.detail?.title || 'course'
      } on ${format(createdAt, 'dd/MM/yyyy')}`,
      certificateUrl: certificate.url || undefined,
    });
  });

  return events;
}
